import { useQuery } from "react-query";
import { Breadcrumb, Col, Row, Skeleton, Typography } from "antd";
import {
  ApartmentOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CustomerServiceOutlined,
  DashboardOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import ReactApexChart from "react-apexcharts";

import { PortalApi } from "../../../services";
import { EmptyState, KpiCard, SectionHeader } from "../../../components";
import { useTheme } from "../../../hooks";
import { toFa } from "../../../utils/helpers";
import styles from "../styles.module.css";

const { Title, Text } = Typography;

/**
 * صفحه‌ی اول پرتال.
 *
 * بدون این، مشتری مستقیم وسط فهرست پروژه‌ها می‌افتاد و هیچ تصویری از کل ماجرا
 * نداشت. اعداد اینجا از همان ستون‌هایی می‌آیند که پروژه به او باز کرده، پس با
 * چیزی که در صفحه‌ی پروژه می‌بیند یکی است.
 */
const PortalDashboard = () => {
  const { isDark, chartColors, chartPalette } = useTheme();

  const { data, isFetching } = useQuery({
    queryKey: ["[Portal] Dashboard"],
    queryFn: () => PortalApi.getPortalDashboard().then((res) => res.data.dashboard),
  });

  return (
    <div className="w-100">
      <Breadcrumb
        className="mb-3"
        items={[
          {
            title: (
              <>
                <HomeOutlined />
                <span>پرتال</span>
              </>
            ),
          },
          { title: "داشبورد" },
        ]}
      />

      <header className={styles.header}>
        <span>
          <DashboardOutlined />
        </span>
        <div>
          <Title level={4}>داشبورد</Title>
          <Text>تصویر کلی کارهایی که برای شما در جریان است</Text>
        </div>
      </header>

      {isFetching && !data ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : (
        <>
          <Row gutter={[12, 12]} className="mb-3">
            <Col xs={24} sm={12} xl={6}>
              <KpiCard
                icon={<ApartmentOutlined />}
                tone="blue"
                label="پروژه‌ها"
                value={toFa(data?.projects || 0)}
              />
            </Col>
            <Col xs={24} sm={12} xl={6}>
              <KpiCard
                icon={<CheckCircleOutlined />}
                tone="green"
                label="کارهای انجام‌شده"
                value={toFa(data?.done_tasks || 0)}
                hint={`از ${toFa(data?.total_tasks || 0)} مورد · ${toFa(data?.progress || 0)}٪`}
              />
            </Col>
            <Col xs={24} sm={12} xl={6}>
              <KpiCard
                icon={<ClockCircleOutlined />}
                tone="orange"
                label="عقب‌افتاده"
                value={toFa(data?.overdue_tasks || 0)}
              />
            </Col>
            <Col xs={24} sm={12} xl={6}>
              <KpiCard
                icon={<CustomerServiceOutlined />}
                tone="purple"
                label="درخواست‌های باز"
                value={toFa(data?.open_tickets || 0)}
              />
            </Col>
          </Row>

          <div className="ds-panel">
            <SectionHeader
              icon={<DashboardOutlined />}
              tone="blue"
              title="روند پیشرفت"
              description="کارهایی که در هشت هفته‌ی گذشته تمام شده‌اند"
            />
            <div className="ds-panel-body">
              {(data?.trend || []).some((item) => item.done > 0) ? (
                <ReactApexChart
                  type="area"
                  height={280}
                  series={[
                    {
                      name: "انجام‌شده",
                      data: (data?.trend || []).map((item) => item.done),
                    },
                  ]}
                  options={{
                    chart: {
                      background: "transparent",
                      foreColor: chartColors.foreColor,
                      toolbar: { show: false },
                      fontFamily: "IRANYekan",
                      zoom: { enabled: false },
                    },
                    colors: [chartPalette[0]],
                    dataLabels: { enabled: false },
                    stroke: { curve: "smooth", width: 3 },
                    fill: {
                      type: "gradient",
                      gradient: {
                        opacityFrom: isDark ? 0.38 : 0.28,
                        opacityTo: 0.03,
                        stops: [0, 95, 100],
                      },
                    },
                    grid: {
                      borderColor: chartColors.gridColor,
                      strokeDashArray: 3,
                      xaxis: { lines: { show: false } },
                    },
                    xaxis: {
                      categories: (data?.trend || []).map((item) => toFa(item.week)),
                      axisBorder: { show: false },
                    },
                    yaxis: {
                      min: 0,
                      forceNiceScale: true,
                      labels: { formatter: (value) => toFa(Math.round(value)) },
                    },
                    tooltip: { theme: isDark ? "dark" : "light" },
                    markers: { size: 0, hover: { size: 5 } },
                  }}
                />
              ) : (
                <EmptyState
                  compact
                  title="هنوز کاری تمام نشده"
                  description="به‌محض اینکه کاری از پروژه‌های شما تمام شود، روندش همین‌جا دیده می‌شود."
                />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PortalDashboard;
